import { Flex, FlexProps, Text, TextProps, Tooltip } from "@chakra-ui/react";
import { RiChat1Line } from "@remixicon/react";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import CompactNumber from "@/common/components/CompactNumber";
import DataCell from "@/common/components/DataCell";
import { DataTable } from "@/common/components/DataTable/DataTable";
import { IcoAutoFeedbackSurveys } from "@/common/components/Icons";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import TimestampCell from "@/common/components/TimestampCell";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSafeDataTableStateProps } from "@/common/hooks/useDataTableParamState";
import { useTableDisplayModeParam } from "@/common/hooks/useTableDisplayMode";
import { createSortableColumnHelper } from "@/common/types/reactTableHelpers";
import { relativeTimeBuckets } from "@/common/utils/datetime";
import { linkClickModifierHandler } from "@/common/utils/linkClickHandler";
import { AccessSummary } from "@/feature/components/AccessSummary";
import { FlagNameCell } from "@/feature/components/FlagNameCell";
import { FlagsEmptyState } from "@/feature/components/FlagsEmptyState";
import { NoStage } from "@/feature/components/RolloutStageTag";
import { TableDisplayModePicker } from "@/feature/components/TableDisplayModePicker";
import { FlagWithSubRows } from "@/feature/data/useFlags";
import { useFlagsSearch } from "@/feature/hooks/useFlagsSearch";
import {
  getDefaultFlagsTableColumns,
  groupingFlagsColumns,
  useFlagsTableState,
} from "@/feature/hooks/useFlagsTableState";
import { StageDisplayName } from "@/stage/components/StageDisplayName";
import { FlagHeaderCollectionSortBy } from "@reflag/shared/flagAPI";
import { sortByFlagColumn } from "@reflag/shared/flagSorting";
import { FlagViewDTO } from "@reflag/shared/flagViewAPI";
import { FeatureUrl } from "@reflag/shared/urls";

type FlagList = {
  data: FlagWithSubRows[];
  metadata?: { sortType: string; viewId: string | undefined };
};

type Props = {
  view: FlagViewDTO;
  data?: FlagList;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

export function FlagsTable({
  view,
  data,
  isLoading,
  isFetching,
  isError,
  onRetry,
}: Props) {
  const { env } = useEnvironment();
  const navigate = useNavigate();
  const tableState = useFlagsTableState(view);

  // We don't want to pass along invalid props to the data table
  const safeTableProps = useSafeDataTableStateProps(tableState);

  const flags = data?.data ?? [];

  // Get display mode and grouping column
  const [displayMode] = useTableDisplayModeParam();

  const filteredFlags = useFlagsSearch(
    flags,
    {
      enabled: true,
      predicate: (f) => {
        const q = tableState.searchQuery.toLowerCase();
        const ownerMatch = f.owner
          ? `${f.owner.name ?? ""} ${f.owner.email ?? ""}`.toLowerCase()
          : "";
        return (
          f.id === tableState.searchQuery ||
          f.key.toLowerCase().includes(q) ||
          f.name.toLowerCase().includes(q) ||
          ownerMatch.includes(q)
        );
      },
    },
    [tableState.searchQuery],
  );

  const columns = useFlagsColumns(env);
  const defaultColumns = getDefaultFlagsTableColumns();

  return (
    <DataTable
      columns={columns}
      data={filteredFlags}
      defaultColumns={defaultColumns}
      displayMode={displayMode}
      emptyState={
        <FlagsEmptyState
          isError={isError}
          isLoading={isLoading}
          view={view}
          onRetry={onRetry}
        />
      }
      groupingSorter={sortByFlagColumn}
      isFetching={isFetching}
      meta={data?.metadata}
      searchPlaceholder="Search by name, key, or ID"
      tableId="flags"
      toolbarRightActions={() => (
        <TableDisplayModePicker<string>
          groupingColumn={tableState.groupingColumn}
          groupingColumns={groupingFlagsColumns}
          onGroupingColumnChange={tableState.onGroupingColumnChange}
        />
      )}
      variant="clickable"
      canCustomize
      canSearch
      canSort
      hasPersistedState
      onRowClick={(row, e) => {
        const url = FeatureUrl(env, {
          id: row.original.featureId,
          name: row.original.name,
        });
        linkClickModifierHandler(url, (e) => {
          e.preventDefault();
          navigate(url);
        })(e);
      }}
      {...safeTableProps}
    />
  );
}

const MultiValueFlexContainer = ({
  children,
  ...props
}: React.PropsWithChildren<FlexProps>) => (
  <Flex
    alignItems="center"
    fontSize="sm"
    gap={2}
    whiteSpace="nowrap"
    {...props}
  >
    {children}
  </Flex>
);

const CellDisplayText = ({
  text,
  icon,
  tooltip,
  textColor,
  iconColor,
  _text,
  ...rest
}: FlexProps & {
  text: string | JSX.Element | null;
  icon?: JSX.Element;
  tooltip?: string;
  textColor?: string;
  iconColor?: string;
  _text?: TextProps;
}) => {
  if (!text) {
    return <NotAvailableCell />;
  }
  return (
    <MultiValueFlexContainer gap={1.5} {...rest}>
      {icon && <Flex color={iconColor ?? "dimmed"}>{icon}</Flex>}
      <Tooltip label={tooltip} openDelay={300} hasArrow>
        <Text color={textColor} {..._text}>
          {text}
        </Text>
      </Tooltip>
    </MultiValueFlexContainer>
  );
};

type FlagListHeaderType = string; // Headers must be strings to support Slack digests

const columnHelper = createSortableColumnHelper<
  FlagWithSubRows,
  FlagHeaderCollectionSortBy,
  FlagListHeaderType
>();

export function useFlagsColumns(env: { name: string }) {
  return useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        cell: FlagNameCell,
        maxSize: document.body.clientWidth / 4,
      }),
      columnHelper.accessor("key", {
        header: "Key",
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <DataCell
              fontFamily="code"
              fontSize="sm"
              type="string"
              value={value}
            />
          );
        },
      }),
      columnHelper.accessor("description", {
        header: "Description",
        enableSorting: false,
        cell: (cell) => {
          const value = cell.getValue();
          return value ? (
            <CellDisplayText
              _text={{
                maxW: "xs",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              text={value}
              tooltip={value}
            />
          ) : (
            <NotAvailableCell />
          );
        },
      }),
      columnHelper.accessor("stage", {
        header: "Stage",
        cell: function StageRenderer(cell) {
          const value = cell.getValue();
          if (value == null) {
            return <NoStage fontSize="sm" />;
          }
          return <StageDisplayName fontSize="sm" stage={value} />;
        },
        getGroupingValue: (row) => {
          return row.stage?.name;
        },
      }),
      columnHelper.accessor("owner", {
        header: "Owner",
        cell: function OwnerRenderer(cell) {
          const value = cell.getValue();
          if (cell.row.getIsGrouped() && !value) {
            return "No owner";
          }
          if (!value) {
            return <NotAvailableCell />;
          }
          return (
            <UserDisplay _avatar={{ size: "2xs" }} user={value} onlyFirstName />
          );
        },
        getGroupingValue: (row) => {
          return row.owner?.name;
        },
      }),
      columnHelper.accessor("targetingSummary", {
        header: `Access in ${env.name}`,
        enableSorting: false,
        cell: function TargetingSummaryRenderer(cell) {
          return (
            <AccessSummary
              fontSize={"sm"}
              targetingSummary={cell.getValue() ?? null}
            />
          );
        },
      }),
      // These columns use custom IDs that match FlagHeaderCollectionSortBy
      // but differ from the accessor keys, so we need to cast them
      {
        id: "lastCheck",
        accessorKey: "lastCheckAt",
        header: "Last check",
        cell: function LastCheck(cell) {
          const value = cell.getValue();
          if (cell.row.getIsGrouped()) {
            return relativeTimeBuckets(value);
          }
          if (!value) {
            return <NotAvailableCell />;
          }
          return <TimestampCell value={value} />;
        },
        getGroupingValue: (row: FlagWithSubRows) => {
          const value = row.lastCheckAt;
          return relativeTimeBuckets(value, true);
        },
      } as ColumnDef<FlagWithSubRows, any>,
      {
        id: "lastTrack",
        accessorKey: "lastTrackAt",
        header: "Last track",
        cell: function LastTrack(cell) {
          const value = cell.getValue() as string | undefined;
          if (cell.row.getIsGrouped()) {
            return relativeTimeBuckets(value);
          }
          if (!value) {
            return <NotAvailableCell />;
          }
          return <TimestampCell value={value} />;
        },
        getGroupingValue: (row: FlagWithSubRows) => {
          const value = row.lastTrackAt;
          return relativeTimeBuckets(value, true);
        },
      } as ColumnDef<FlagWithSubRows, any>,
      columnHelper.accessor("feedbackCount", {
        header: "Feedback",
        enableSorting: false,
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <CellDisplayText
              aria-label={`Based on ${value} submissions`}
              icon={<RiChat1Line size="16" />}
              text={value ? <CompactNumber value={value} /> : null}
            />
          );
        },
      }),
      {
        id: "autoFeedbackSurveysEnabled",
        accessorKey: "automatedSurveysEnabled",
        header: "Automated feedback surveys",
        cell: (cell) => {
          const value = cell.getValue() as boolean | undefined;
          if (cell.row.getIsGrouped() && !value) {
            return "Disabled";
          }
          return (
            <CellDisplayText
              icon={<IcoAutoFeedbackSurveys aria-hidden="true" size="md" />}
              text={value ? "Enabled" : null}
            />
          );
        },
        getGroupingValue: (row: FlagWithSubRows) => {
          return row.automatedSurveysEnabled ? "Enabled" : "Disabled";
        },
      } as ColumnDef<FlagWithSubRows, any>,
      columnHelper.accessor("createdAt", {
        header: "Created",
        cell: (cell) => {
          const value = cell.getValue();
          if (cell.row.getIsGrouped()) {
            return relativeTimeBuckets(value);
          }
          if (!value) {
            return <NotAvailableCell />;
          }
          return <TimestampCell value={value} />;
        },
        getGroupingValue: (row) => {
          const value = row.createdAt;
          return relativeTimeBuckets(value, true);
        },
      }),
    ],
    [env],
  );
}
